import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AIDiscoveryIntroCard from './ai-discovery/AIDiscoveryIntroCard';
import { useThemeSettings } from '@/contexts/ThemeContext';
import { useCompactViewAll } from '@/hooks/useCompactViewAll';
import UnifiedProgramCard from '@/components/cards/UnifiedProgramCard';
import CompactOrFullAction from '@/components/ui/CompactOrFullAction';

const DiscoveredCampCard = ({ 
  camp, 
  onFavorite, 
  onSaveForLater, 
  onDelete,
  onViewDetails,
  onSave,
  isSaved = false
}) => {
  const navigate = useNavigate();
  const { theme } = useThemeSettings();
  const isDarkTheme = theme === 'dark';
  
  // Helper to parse age range
  const parseAgeRange = (ageStr) => {
    if (!ageStr) return { age_range: null, age_min: null, age_max: null };
    
    // Handle formats like "8-12", "8 - 12", "8", "Ages 8-12"
    const cleaned = ageStr.toString().replace(/ages?\s*/i, '').trim();
    const parts = cleaned.split(/[-–—]/).map(p => p.trim());
    
    if (parts.length === 2) {
      const min = parseInt(parts[0]);
      const max = parseInt(parts[1]);
      if (!isNaN(min) && !isNaN(max)) {
        return {
          age_range: `Ages ${min}-${max}`,
          age_min: min,
          age_max: max
        };
      }
    } else if (parts.length === 1) {
      const age = parseInt(parts[0]);
      if (!isNaN(age)) {
        return {
          age_range: `Ages ${age}`,
          age_min: age,
          age_max: age
        };
      }
    }
    
    return {
      age_range: ageStr.toString().startsWith('Ages') ? ageStr : `Ages ${ageStr}`,
      age_min: null,
      age_max: null
    };
  };
  
  const ageData = parseAgeRange(camp.child_age);
  
  // Map discovered camp data to UnifiedProgramCard format
  const programData = {
    id: camp.id || camp.camp_id,
    name: camp.camp_name || camp.name,
    title: camp.camp_name || camp.name,
    organization: camp.camp_organization,
    location: camp.camp_address || camp.camp_location,
    formatted_address: camp.camp_address || camp.camp_location,
    general_location: camp.camp_address || camp.camp_location,
    address: camp.camp_address || camp.camp_location,
    cost_total: camp.cost_total || camp.camp_cost,
    regularPrice: camp.cost_total || camp.camp_cost,
    price: camp.cost_total || camp.camp_cost ? `$${camp.cost_total || camp.camp_cost}` : null,
    age_range: ageData.age_range,
    age_min: ageData.age_min,
    age_max: ageData.age_max,
    image_url: camp.image_url || camp.photo_url,
    photo_url: camp.image_url || camp.photo_url,
    category: camp.category || 'Camp',
    primary_category: camp.category || 'Camp',
    // Email source info stored in program_data for reference
    program_data: {
      ...camp,
      email_source: camp.camp_email
    }
  };
  
  // Handle delete - UnifiedProgramCard passes program object, but handler expects camp ID
  const handleDelete = (program) => {
    if (onDelete && camp.id) {
      onDelete(camp.id);
    }
  };
  
  // Handle save - UnifiedProgramCard passes program object, but handler expects camp object
  const handleSave = (program) => {
    if (onSaveForLater) {
      onSaveForLater(camp);
    }
  };
  
  // Handle edit - navigate to universal program page
  const handleEdit = () => {
    const programId = camp.id || camp.camp_id || programData.id;
    if (programId) {
      navigate(`/program/${programId}?mode=discovery&source=email_scout`);
    }
  };

  // Handle view - navigate to universal program page
  const handleView = () => {
    const programId = camp.id || camp.camp_id || programData.id;
    if (programId) {
      navigate(`/program/${programId}?mode=discovery&source=email_scout`);
    }
  };

  return (
    <UnifiedProgramCard
      program={programData}
      viewMode="saved"
      variant="horizontal"
      isSaved={isSaved}
      hideCategoryBadge={true}
      statusInlineWithTitle={true}
      uniformHeight={true}
      flatListing={true}
      onClick={handleView}
      onView={handleView}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onSave={handleSave}
    />
  );
};

const DashboardEmailDiscoverySection = ({ 
  discoveredCamps = [], 
  totalDiscovered = 0,
  isEmailConnected = false,
  messagesCount = 0,
  isLoading = false,
  onViewAllDiscoveries,
  onFavoriteCamp,
  onSaveForLater,
  onDeleteCamp,
  onConnectEmail,
  onSaveCamp,
  savedProgramIds = new Set()
}) => {
  const { theme } = useThemeSettings();
  const isDarkTheme = theme === 'dark';
  const compactViewAll = useCompactViewAll();

  // Filter camps to only include those with valid camp_name
  const validCamps = (discoveredCamps || []).filter((camp) => 
    camp.camp_name && camp.camp_name.trim() !== ''
  );
  
  // Show maximum 3 camps on dashboard
  const displayCamps = validCamps.slice(0, 3);
  
  // Calculate actual count of valid camps
  const validCampsCount = validCamps.length;
  
  // Helper to get camp identifier for saved state checking
  const getCampIdentifier = (camp) => {
    return camp.id || camp.camp_id || `${camp.camp_name || camp.name}-${camp.camp_organization || ''}`;
  };
  
  // Show loading state
  if (isLoading) {
    return (
      <section className="mb-8">
        <div className="overflow-hidden rounded-xl border border-purple-100/70 shadow-xl">
          <div className={`p-6 border-b ${isDarkTheme ? 'border-slate-700/40 bg-gradient-to-r from-slate-900/90 to-slate-800/90' : 'border-gray-100 bg-gradient-to-r from-purple-50 to-blue-50'}`}>
            <div className="flex items-center gap-3">
              <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${isDarkTheme ? 'bg-slate-800' : 'bg-gradient-to-br from-blue-100 via-blue-50 to-purple-100'} text-brand-600 shadow-inner`}>
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <h2 className={`text-xl font-bold sm:text-2xl leading-tight ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>
                  Email Scout
                </h2>
                <p className={`text-xs sm:text-sm mt-0.5 ${isDarkTheme ? 'text-slate-400' : 'text-gray-600'}`}>
                  Loading discovered camps...
                </p>
              </div>
            </div>
          </div>
          <div className={`${isDarkTheme ? 'bg-slate-900' : 'bg-white'} p-12 text-center`}>
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite] text-brand-600"></div>
            <p className={`mt-4 text-sm ${isDarkTheme ? 'text-slate-400' : 'text-slate-600'}`}>
              Loading discovered camps...
            </p>
          </div>
        </div>
      </section>
    );
  }
  
  // Show intro card when email not connected AND no valid camps
  if (!isEmailConnected && validCampsCount === 0) {
    return (
      <div className="mb-6">
        <AIDiscoveryIntroCard 
          onConnectEmail={onConnectEmail}
          isEmailConnected={isEmailConnected}
          discoveredCampsCount={validCampsCount}
          messagesCount={messagesCount}
          variant="dashboard" 
        />
      </div>
    );
  }
  
  // Don't show section if no valid camps to display (whether email connected or not)
  if (validCampsCount === 0) {
    return null;
  }
  
  return (
    <section className="mb-8">
      <div className="overflow-hidden rounded-xl border border-purple-100/70 shadow-xl transition-shadow duration-200 hover:shadow-2xl">
        {/* Header - Enhanced with icon and description */}
          <div className={`p-6 border-b ${isDarkTheme ? 'border-slate-700/40 bg-gradient-to-r from-slate-900/90 to-slate-800/90' : 'border-gray-100 bg-gradient-to-r from-purple-50 to-blue-50'}`}>
            <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${isDarkTheme ? 'bg-slate-800' : 'bg-gradient-to-br from-blue-100 via-blue-50 to-purple-100'} text-brand-600 shadow-inner`}>
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h2 className={`text-xl font-bold sm:text-2xl leading-tight ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>
                  Email Scout
                </h2>
                <p className={`text-xs sm:text-sm mt-0.5 ${isDarkTheme ? 'text-slate-400' : 'text-gray-600'}`}>
                  {displayCamps.length > 0 && validCampsCount > 0
                    ? `Showing ${displayCamps.length} of ${validCampsCount} discovered camp${validCampsCount !== 1 ? 's' : ''}`
                    : 'Camps discovered from your emails'}
                </p>
              </div>
            </div>
            <CompactOrFullAction
              compact={compactViewAll}
              onClick={() => {
                if (onViewAllDiscoveries) {
                  onViewAllDiscoveries();
                } else {
                  window.location.href = '/email-scout-details';
                }
              }}
              label="View All"
              ariaLabel="View all discovered camps"
              isDarkTheme={isDarkTheme}
            />
          </div>
        </div>

        <div className={`${isDarkTheme ? 'bg-slate-900' : 'bg-white'} p-6 md:p-8 lg:p-10`}>
          <div className="[&>*:last-child]:border-b-0">
            {displayCamps.map((camp, index) => {
              const campIdentifier = getCampIdentifier(camp);
              const isSaved = savedProgramIds.has(campIdentifier);
              
              return (
                <DiscoveredCampCard
                  key={camp.id ? `camp-${camp.id}` : `discovered-camp-${index}`}
                  camp={camp}
                  onFavorite={onFavoriteCamp}
                  onSaveForLater={onSaveForLater}
                  onDelete={onDeleteCamp}
                  onSave={onSaveCamp}
                  isSaved={isSaved}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardEmailDiscoverySection;
